import * as React from 'react';
import Grid from '@mui/material/Grid2';
import { observer } from 'mobx-react';
import BaseButton from '../../../../components/BaseButton';
import BaseInput from '../../../../components/BaseInput';
import DataStore from '../../../../store';
import { validateForm } from '../../../../helpers/formValidator';
import { notificationStore } from '../../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../../helpers/constants';
import { getIdFromLocalStorage } from '../../../../helpers/base';
import socketStore from '../../../../store/Socket';
import ProgressBar from '../../../../components/ProgressBar';
import { validationRules } from '../../../../helpers/validationRules';
import { Box } from '@mui/material';
import { PREVIEW_BUTTON } from '../../../../constants/base';
import { checkDownloadStatusAndDownload } from '../../../../helpers/request';
import { useSocketListener } from '../../../../hooks/useSocketListener';
import { ProgressPayload } from '../../../../types/global';

const Download = observer(() => {
  const [progress, setProgress] = React.useState(0);
  const [progressMax, setProgressMax] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState('');

  React.useEffect(() => {
    showOutput();
  }, []);

  const socket = socketStore.socket;
  const channel = React.useMemo(
    () => `user_update_${getIdFromLocalStorage()}`,
    []
  );
  const onProgress = React.useCallback((data: ProgressPayload): void => {
    setProgress(data.progress ?? 0);
    setProgressMax(data.total ?? 0);
    setProgressMessage(data.message ?? '');
  }, []);
  useSocketListener<ProgressPayload>(socket, channel, onProgress, true);

  const fieldLabels: { [key: string]: string } = {
    s3Link: 'S3 Link',
    keyword: 'Keyword',
  };

  const validationSchema = {
    ...{
      s3Link: validationRules.s3Link,
      keyword: validationRules.keyword,
    },
  };
  const [errors, setErrors] = React.useState({});

  const [data, setData] = React.useState({
    s3Link: '',
    keyword: '',
  });

  const onChangeData = (event: any) => {
    const { name, value }: any = event.target;
    setData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  function showOutput(resData: any = {}): void {
    DataStore.showOutput('Preview download', resData, 'html');
  }

  const resetProgressBar = () => {
    setProgress(0);
    setProgressMax(0);
    setProgressMessage('');
  };

  const previewRun = async (e: any) => {
    e.preventDefault();
    setProgress(0);
    setProgressMax(100);
    setProgressMessage('Starting...');
    const validError = validateForm(data, validationSchema);
    if (!validError) {
      setErrors({});
      const resData: any = await DataStore.downloadPreviewRequest(data);
      if (resData?.messageType === 'error') {
        setProgressMessage('');
        DataStore.showOutput('Preview download', resData, 'html');
      } else if (resData) {
        showOutput(resData);
        resetProgressBar();
      }
    } else {
      setProgressMessage('');
      const isRequiredMissing = (Object.values(validError) as string[]).some(
        (msg) => msg.toLowerCase().includes('is required')
      );
      const humanizedErrors = Object.entries(validError).reduce(
        (acc: any, [key, message]) => {
          const label = fieldLabels[key] || key;
          const safeMessage =
            typeof message === 'string' ? message : String(message);
          acc[label] = safeMessage.replace(new RegExp(key, 'gi'), label);
          return acc;
        },
        {}
      );
      notificationStore.showMessage(
        isRequiredMissing ? MISSING_FIELDS_MESSAGE : 'Some fields are invalid.',
        'error',
        humanizedErrors
      );
      setErrors(humanizedErrors);
    }
  };

  const launchRun = async () => {
    setProgress(0);
    setProgressMax(0);
    setProgressMessage('Starting...');
    try {
      const res = await DataStore.downloadLaunchRequest();
      if (res.messageType === 'error') {
        notificationStore.showMessage(res.message, 'error');
        return;
      }

      const jobId = res.response?.download_id;
      if (!jobId) {
        notificationStore.showMessage('Unexpected response format.', 'error');
        return;
      }

      notificationStore.showMessage('Preparing download...', 'info');

      await checkDownloadStatusAndDownload(jobId);

      notificationStore.showMessage('Download completed.', 'success');
    } catch (err: any) {
      notificationStore.showMessage(err.message || 'Download failed.', 'error');
      return;
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12 }}>
          <BaseInput
            feature={'download'}
            label={fieldLabels.s3Link}
            value={data.s3Link}
            name={'s3Link'}
            onChange={onChangeData}
            errors={errors}
            required={!!validationSchema?.s3Link}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <BaseInput
            feature={'download'}
            label={fieldLabels.keyword}
            value={data.keyword}
            name={'keyword'}
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <Box display='flex' alignItems='center' gap={1}>
            <BaseButton
              text={PREVIEW_BUTTON}
              variant='outlined'
              color='primary'
              onClick={previewRun}
              isDisabled={DataStore.isProcessing}
              spinner={DataStore.isProcessing}
              customStyle={{ marginRight: '10px' }}
            />
            <BaseButton
              text='DOWNLOAD'
              variant='contained'
              color='primary'
              isDisabled={
                DataStore.isProcessing || !DataStore.isDownloadPreview
              }
              spinner={DataStore.isProcessing}
              onClick={launchRun}
            />
          </Box>
        </Grid>
      </Grid>
      <ProgressBar
        actual={progress}
        max={progressMax}
        message={progressMessage}
      />
    </>
  );
});

export default Download;
